import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { FC, useCallback, useEffect, useState } from 'react';
import { Setting } from '../../types/constant/PagesPropsType';
import confirmModalClass from '../../assets/scss/ConfirmModal/ConfirmModal.module.scss';
import { useUpdateNextDeliveryDate } from '../../hooks/use-update-next-delivery-date';
import { SubscriptionApiType } from '../../types/api/SubscriptionApiType';
import { UpdateMessageModalViewType } from '../../types/view/MessageModalViewType';
import { SkipModalViewType } from '../../types/view/SkipModalViewType';
import { SummaryViewType } from '../../types/view/SummaryViewType';
import { CommonModal } from '../CommonModal';
import { postMessageGtag } from '../../utils/trackClick';
import { usePreventionData } from '../../hooks/use-master-data';
import { PreventionImages } from '../ModalItem/PreventionImages';

dayjs.locale('ja');

export const SkipModal: FC<{
  active: boolean;
  closeFunc: () => void;
  skipModal: SkipModalViewType | null;
  summary?: SummaryViewType;
  setting: Setting;
  loading: boolean;
  subscription: SubscriptionApiType;
  toggleLoading: React.Dispatch<React.SetStateAction<boolean>>;
  toggleMessageModal: React.Dispatch<React.SetStateAction<UpdateMessageModalViewType | null>>;
  toggleSubscription: React.Dispatch<React.SetStateAction<SubscriptionApiType | undefined>>;
  onSwitchToRecommended: () => void;
}> = ({
  active,
  closeFunc,
  skipModal,
  summary,
  setting,
  loading,
  subscription,
  toggleLoading,
  toggleMessageModal,
  toggleSubscription,
  onSwitchToRecommended,
}) => {
  const [skipSpan, setSkipSpan] = useState<0 | 2 | 4>(0);
  const { skipDeliveryDays } = useUpdateNextDeliveryDate();

  const { preventionList } = usePreventionData('skip_prevention');

  useEffect(() => {
    // 閉じたら選択をなしに
    if (!active) {
      setSkipSpan(0);
    }
  }, [active]);

  const clickOkBtnHandler = useCallback(() => {
    if (loading) return;

    toggleLoading(true);
    skipDeliveryDays(
      setting,
      subscription!,
      skipSpan,
      (nextBillingDate, nextDeliveryDate, deliveryDays, deliveryTime, deliveryTimeText) => {
        toggleSubscription({
          ...subscription!,
          nextBillingDate,
          nextDeliveryDate,
          deliveryDays,
          deliveryTime,
          deliveryTimeText,
        });
        toggleMessageModal({
          isSuccess: true,
        });
      },
      () =>
        toggleMessageModal({
          isSuccess: false,
        }),
      () => {
        toggleLoading(false);
        closeFunc();
      }
    );
  }, [skipModal, skipSpan, loading]);

  const onClickSwitchToRecommend = useCallback(() => {
    onSwitchToRecommended();
    postMessageGtag('click_Switch_skip', 'detailCalender');
  }, [onSwitchToRecommended]);

  const onClickOutsideClose = useCallback(() => {
    closeFunc();
    postMessageGtag('click_modal_close_skip', 'detailCalender');
  }, [closeFunc]);

  if (!skipModal) return <></>;

  const content = (
    <>
      <div className={confirmModalClass.skipTitle}>次回のお届けをスキップする</div>
      <div className={confirmModalClass.skipSpanContainer}>
        <label className={confirmModalClass.skipSpanLabel}>
          <input
            type="radio"
            name="skipSpan"
            value={2}
            checked={skipSpan === 2}
            onChange={() => setSkipSpan(2)}
          />
          2週間後
        </label>

        <label className={confirmModalClass.skipSpanLabel}>
          <input
            type="radio"
            name="skipSpan"
            value={4}
            checked={skipSpan === 4}
            onChange={() => setSkipSpan(4)}
          />
          4週間後
        </label>
      </div>
      <div className={confirmModalClass.changedDateContainer}>
        <div className={confirmModalClass.changedDateTitle}>変更後のお届け日</div>
        <div className={confirmModalClass.changedDate}>
          {dayjs(summary?.nextDeliveryDate).add(skipSpan, 'w').format('YYYY/M/D（ddd）')}
        </div>
      </div>
      <div className={confirmModalClass.btnContainer}>
        <button
          onClick={() => {
            if (loading || skipSpan === 0) return;
            clickOkBtnHandler();
            postMessageGtag('click_skip', 'detailCalender');
          }}
          disabled={loading || skipSpan === 0}
        >
          スキップする
        </button>
      </div>
      <div className={confirmModalClass.divider}>
        <span>または</span>
      </div>
      <div className={confirmModalClass.leadText}>
        <button
          onClick={onClickSwitchToRecommend}
          className={confirmModalClass.recommendedSetLink}
        >
          別の商品に切り替える
        </button>
      </div>
      {/* スキップ対策 */}
      <PreventionImages
        preventionList={preventionList}
        route="skip"
      />
    </>
  );

  return (
    <CommonModal
      active={active}
      closeFunc={onClickOutsideClose}
      loading={loading}
    >
      {content}
    </CommonModal>
  );
};
